import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { I, Icon } from '../shared/ns-icons'
import { nsConfirm } from '../shared/ns-confirm'

// ── Helpers ───────────────────────────────────────────────────────────────────

// action → badge label + tone (matches the prototype's NS_ACTION_TONE)
const ACTION_MAP = {
  create:     { label: 'created',   tone: 'success' },
  update:     { label: 'updated',   tone: 'info' },
  delete:     { label: 'deleted',   tone: 'danger' },
  publish:    { label: 'published', tone: 'accent' },
  save_draft: { label: 'draft',     tone: 'warning' },
  restore:    { label: 'reverted',  tone: 'warning' },
}

const EDIT_TYPE_LABELS = {
  text_change: 'Text', new_page: 'New page', visual_edit: 'Visual edit',
  component_add: 'Component added', component_delete: 'Component removed',
  style_change: 'Style', publish: 'Publish', save_draft: 'Draft',
  media_upload: 'Media', settings_change: 'Settings', domain_add: 'Domain',
  domain_verify: 'Domain', domain_delete: 'Domain', domain_update: 'Domain',
  restore: 'Revert',
}

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'publish', label: 'Published' },
  { value: 'save_draft', label: 'Draft saved' },
  { value: 'restore', label: 'Reverted' },
]

const RANGE_OPTIONS = [
  { value: '', label: 'All time' },
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

const actionOf = (log) => ACTION_MAP[log.action] || { label: log.action || 'edit', tone: 'info' }

const editTypeLabel = (t) =>
  !t ? 'Edit' : (EDIT_TYPE_LABELS[t] || t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))

const sectionOf = (log) => {
  if (Array.isArray(log.affected_elements) && log.affected_elements.length) {
    return log.affected_elements[0]
  }
  return editTypeLabel(log.edit_type)
}

// Human names for change kinds, used in the "Change summary" column.
const KIND_LABELS = { css: 'CSS', html: 'HTML', text: 'Text', javascript: 'JavaScript' }

// Fallback when a log predates change-kind detection.
const EDIT_TYPE_KINDS = {
  style_change: ['css'], text_change: ['text'], new_page: ['html'],
  visual_edit: ['html'], component_add: ['html'], component_delete: ['html'],
}

const changeKindsOf = (log) => {
  if (Array.isArray(log.changes?.changeKinds) && log.changes.changeKinds.length) {
    return log.changes.changeKinds
  }
  return EDIT_TYPE_KINDS[log.edit_type] || []
}

const listJoin = (arr) =>
  arr.length <= 1 ? (arr[0] || '')
  : arr.length === 2 ? `${arr[0]} and ${arr[1]}`
  : `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`

// Readable summary like "CSS updated" / "HTML and Text updated".
const summaryTextOf = (log) => {
  const kinds = changeKindsOf(log)
  if (kinds.length) {
    const verb = log.action === 'create' ? 'added' : log.action === 'delete' ? 'removed' : 'updated'
    return `${listJoin(kinds.map(k => KIND_LABELS[k] || k))} ${verb}`
  }
  return log.description || '—'
}

// Fallback before/after (raw text) reconstructed from the line diff.
const beforeAfterOf = (log) => {
  const d = log.diff_data
  if (d) {
    const before = [
      ...(d.modified || []).map(m => m.before),
      ...(d.removed || []).map(r => r.content),
    ].filter(s => s != null && s !== '').join('\n')
    const after = [
      ...(d.modified || []).map(m => m.after),
      ...(d.added || []).map(a => a.content),
    ].filter(s => s != null && s !== '').join('\n')
    if (before || after) return { before: before || '—', after: after || '—' }
  }
  if (log.action === 'publish') return { before: 'Draft', after: 'Published · live' }
  if (log.action === 'create')  return { before: '—', after: log.description || 'Created' }
  if (log.action === 'delete')  return { before: log.description || 'Removed', after: '—' }
  if (log.action === 'restore') return { before: '—', after: log.description || 'Reverted' }
  return { before: '—', after: log.description || '—' }
}

const initialsOf = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()

const gradOf = (name) => {
  let h = 0
  for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return (h % 6) + 1
}

// SQLite stores created_at as a UTC string ("YYYY-MM-DD HH:MM:SS", no zone).
// Browsers parse that space-separated form as *local* time, so we must mark it
// as UTC ourselves, otherwise times render shifted by the local offset.
const parseTs = (ts) => {
  if (!ts) return new Date(NaN)
  const s = String(ts)
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s) // already has a zone
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(s)
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]))
  return new Date(s)
}

const fmtTime = (ts) => {
  const d = parseTs(ts)
  if (isNaN(d)) return '—'
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

const fmtDay = (ts) => {
  const d = parseTs(ts)
  if (isNaN(d)) return ''
  const now = new Date()
  const sameDay = (a, b) => a.toDateString() === b.toDateString()
  if (sameDay(d, now)) return 'Today'
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (sameDay(d, y)) return 'Yesterday'
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const GRID = '120px 1.3fr 1.3fr 1.4fr 1fr 1.6fr 28px'

// Build readable Before/After text blocks from the structured change preview:
// CSS/inline-style changes are grouped per selector with their properties;
// text changes are listed below.
const groupedPreview = (items) => {
  const byScope = new Map()
  const textBefore = []
  const textAfter = []
  for (const it of items) {
    if (it.type === 'css' && it.property) {
      const key = it.selector || 'element'
      if (!byScope.has(key)) byScope.set(key, [])
      byScope.get(key).push(it)
    } else {
      if (it.before != null && it.before !== '') textBefore.push(it.before)
      if (it.after != null && it.after !== '') textAfter.push(it.after)
    }
  }
  const beforeLines = []
  const afterLines = []
  for (const [scope, props] of byScope) {
    const b = props.filter(p => p.op !== 'added').map(p => `  ${p.property}: ${p.before}`)
    const a = props.filter(p => p.op !== 'removed').map(p => `  ${p.property}: ${p.after}`)
    if (b.length) beforeLines.push(scope, ...b, '')
    if (a.length) afterLines.push(scope, ...a, '')
  }
  if (textBefore.length) beforeLines.push(...textBefore)
  if (textAfter.length) afterLines.push(...textAfter)
  return {
    before: beforeLines.join('\n').trim() || '—',
    after: afterLines.join('\n').trim() || '—',
  }
}

// Readable Before/After for a log — structured preview when present, raw diff otherwise.
const previewOf = (log) => {
  const items = log.changes?.changePreview || []
  return items.length ? groupedPreview(items) : beforeAfterOf(log)
}

// ── Expandable log row ────────────────────────────────────────────────────────

function LogRow({ log, open, onToggle, onView, onEditor, onRevert, reverting }) {
  const action = actionOf(log)
  const { before, after } = previewOf(log)
  const isReverted = log.reverted || log.action === 'restore'

  return (
    <>
      <div className="row" style={{ gridTemplateColumns: GRID, cursor: 'pointer' }} onClick={onToggle}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
          <div style={{ color: 'var(--text-soft)' }}>{fmtTime(log.created_at)}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>{fmtDay(log.created_at)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span className={`avatar avatar-grad-${gradOf(log.user_name)}`} style={{ width: 24, height: 24, fontSize: 9 }}>
            {initialsOf(log.user_name)}
          </span>
          <span className="truncate" style={{ color: 'var(--text)' }}>{log.user_name || 'Unknown'}</span>
        </div>
        <div className="truncate" style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)' }}>
          {log.site_name || '—'}
        </div>
        <div className="truncate" style={{ color: 'var(--text-soft)' }}>{sectionOf(log)}</div>
        <div>
          <span className={`badge badge-${action.tone}`} style={{ fontSize: 10 }}>{action.label}</span>
        </div>
        <div className="truncate" style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{summaryTextOf(log)}</div>
        <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-muted)' }}>
          <I.chevronDown style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s ease' }} />
        </div>
      </div>

      {open && (
        <div style={{ gridColumn: '1 / -1', padding: '4px 18px 18px 138px', borderTop: '1px solid var(--border)' }}>
          <div className="drawer-section-label" style={{ marginTop: 12 }}>Change preview</div>

          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '7px 12px', background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <I.x style={{ fontSize: 12 }} /> Before
              </div>
              <div style={{ padding: '12px 14px', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-soft)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflowY: 'auto' }}>
                {before}
              </div>
            </div>
            <div style={{ borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '7px 12px', background: 'var(--success-soft)', color: 'var(--success)', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <I.check style={{ fontSize: 12 }} /> After
              </div>
              <div style={{ padding: '12px 14px', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflowY: 'auto' }}>
                {after}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-sm" onClick={onView} disabled={!log.site_id}><I.eye /> View on site</button>
            <button className="btn btn-sm" onClick={onEditor} disabled={!log.site_id}><I.edit /> Open in editor</button>
            <button
              className="btn btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={onRevert}
              disabled={!log.revertible || isReverted || reverting}
              title={
                isReverted ? 'This change has already been reverted'
                : !log.revertible ? 'This change can’t be reverted'
                : 'Restore the affected page/site to its state before this change'
              }
            >
              {isReverted ? 'Reverted' : reverting ? 'Reverting…' : 'Revert this change'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// ── Bento stat card ───────────────────────────────────────────────────────────

function StatBento({ label, value, sub, icon, tone }) {
  return (
    <div className="card card-pad-sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span className={`icon-tile ${tone || ''}`} style={{ width: 28, height: 28, fontSize: 13 }}><Icon name={icon} /></span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

// ── Popover + filter dropdown ─────────────────────────────────────────────────

const MENU_STYLE = {
  position: 'absolute', top: 'calc(100% + 6px)', zIndex: 40,
  minWidth: 190, maxHeight: 300, overflowY: 'auto',
  background: 'var(--bg-1)', border: '1px solid var(--border-strong)',
  borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6,
}

const menuItemStyle = (active) => ({
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '8px 10px', borderRadius: 7, fontSize: 12.5, cursor: 'pointer',
  border: 'none', background: active ? 'var(--surface-hover)' : 'transparent',
  color: active ? 'var(--text)' : 'var(--text-soft)', fontFamily: 'var(--font)',
})

function Popover({ children, render, align = 'left' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {children(open, setOpen)}
      {open && (
        <div style={{ ...MENU_STYLE, [align]: 0 }}>
          {render(setOpen)}
        </div>
      )}
    </div>
  )
}

function FilterDropdown({ icon, label, value, options, onChange, align }) {
  const sel = options.find(o => o.value === value && o.value !== '')
  return (
    <Popover
      align={align}
      render={(setOpen) => options.map(o => {
        const active = o.value === value
        return (
          <button key={o.value || 'all'} style={menuItemStyle(active)} onClick={() => { onChange(o.value); setOpen(false) }}>
            <span className="truncate">{o.label}</span>
            {active && <span style={{ marginLeft: 'auto' }}><I.check style={{ fontSize: 13 }} /></span>}
          </button>
        )
      })}
    >
      {(open, setOpen) => (
        <button
          className="btn btn-sm"
          onClick={() => setOpen(o => !o)}
          style={{ whiteSpace: 'nowrap', borderColor: sel ? 'var(--accent)' : undefined, color: sel ? 'var(--text)' : undefined }}
        >
          {icon} {sel ? sel.label : label} <I.chevronDown style={{ fontSize: 12 }} />
        </button>
      )}
    </Popover>
  )
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ pagination, onPageChange }) {
  if (!pagination || pagination.pages <= 1) return null
  const { page, pages, total, limit } = pagination
  const from = ((page - 1) * limit) + 1
  const to = Math.min(page * limit, total)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{from}–{to} of {total}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button className="btn btn-sm" onClick={() => onPageChange(page - 1)} disabled={page === 1}>Prev</button>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '0 4px' }}>{page} / {pages}</span>
        <button className="btn btn-sm" onClick={() => onPageChange(page + 1)} disabled={page === pages}>Next</button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { range: '', siteId: '', userId: '', editType: '', action: '' }

const EditLogsDashboard = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState(null)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const limit = 50

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1) }
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1) }
  const activeFilterCount = Object.values(filters).filter(Boolean).length

  // Build the query string from page + filters
  const queryString = (() => {
    const p = new URLSearchParams()
    p.set('page', page); p.set('limit', limit)
    if (filters.siteId) p.set('siteId', filters.siteId)
    if (filters.userId) p.set('userId', filters.userId)
    if (filters.editType) p.set('editType', filters.editType)
    if (filters.action) p.set('action', filters.action)
    if (filters.range) {
      const days = filters.range === '1d' ? 1 : filters.range === '7d' ? 7 : 30
      const d = new Date(Date.now() - days * 86400000)
      p.set('startDate', d.toISOString().slice(0, 19).replace('T', ' '))
    }
    return p.toString()
  })()

  const { data: logsData, isLoading, error, refetch } = useQuery(
    ['edit-logs', page, filters],
    () => apiService.get(`/edit-logs?${queryString}`).then(res => res.data),
    { keepPreviousData: true, staleTime: 60000, refetchOnWindowFocus: false }
  )

  const { data: stats } = useQuery(
    ['edit-stats'],
    () => apiService.get('/edit-logs/stats').then(res => res.data),
    { staleTime: 60000, refetchOnWindowFocus: false }
  )

  // Filter option sources
  const { data: sitesData } = useQuery(['sites'], () => apiService.get('/sites').then(r => r.data), { staleTime: 60000, refetchOnWindowFocus: false })
  const { data: usersData } = useQuery(['users-list'], () => apiService.get('/users').then(r => r.data), { staleTime: 60000, refetchOnWindowFocus: false })
  const { data: metadata } = useQuery(['edit-metadata'], () => apiService.get('/edit-logs/metadata').then(r => r.data), { staleTime: 300000, refetchOnWindowFocus: false })

  const siteOptions = [{ value: '', label: 'All sites' }, ...((sitesData?.sites || []).map(s => ({ value: String(s.id), label: s.name })))]
  const userOptions = [{ value: '', label: 'All users' }, ...((Array.isArray(usersData) ? usersData : []).map(u => ({ value: String(u.id), label: u.name || u.email })))]
  const editTypeOptions = [{ value: '', label: 'All edit types' }, ...((metadata?.editTypes || []).map(t => ({ value: t.value, label: t.label })))]

  const revertMutation = useMutation(
    (id) => apiService.post(`/edit-logs/${id}/revert`).then(r => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['edit-logs'])
        queryClient.invalidateQueries(['edit-stats'])
      },
      onError: (err) => window.alert(err?.response?.data?.error || 'Failed to revert this change'),
    }
  )

  const handleRevert = async (log) => {
    const ok = await nsConfirm({
      tone: 'warning',
      icon: 'clock',
      title: 'Revert this change?',
      message: 'The affected page or site will be restored to its state before this edit.',
      detail: 'A new edit-log entry records the revert',
      confirmLabel: 'Revert change',
    })
    if (!ok) return
    revertMutation.mutate(log.id)
  }

  const exportLogs = async () => {
    try {
      const res = await apiService.get('/edit-logs?limit=1000')
      const rows = res.data?.logs || []
      const headers = ['Timestamp', 'User', 'Site', 'Section', 'Action', 'Change types', 'Summary', 'Reverted']
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
      const csv = [headers.join(',')]
        .concat(rows.map(l => [
          parseTs(l.created_at).toISOString(),
          l.user_name, l.site_name, sectionOf(l),
          actionOf(l).label,
          changeKindsOf(l).map(k => KIND_LABELS[k] || k).join(' / '),
          summaryTextOf(l), l.reverted ? 'yes' : 'no',
        ].map(esc).join(',')))
        .join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `edit-logs-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      window.alert('Failed to export edit logs')
    }
  }

  const allLogs = logsData?.logs || []
  const q = search.trim().toLowerCase()
  const logs = q
    ? allLogs.filter(l =>
        [l.description, l.site_name, l.user_name, sectionOf(l)]
          .filter(Boolean).some(v => v.toLowerCase().includes(q)))
    : allLogs

  // Bento numbers (per-type stats aggregated; distinct counts approximated via max).
  const totalEdits = stats?.reduce((s, x) => s + (x.count || 0), 0) || 0
  const sitesEdited = stats?.length ? Math.max(...stats.map(x => x.unique_sites || 0)) : 0
  const activeEditors = stats?.length ? Math.max(...stats.map(x => x.unique_users || 0)) : 0
  const avgPerDay = totalEdits ? (totalEdits / 30).toFixed(1) : '0'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>

      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Audit</div>
          <h1>Edit logs</h1>
          <div className="subtitle">Every content change across all client sites</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn" onClick={exportLogs}><I.upload /> Export CSV</button>
        </div>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Stat bento */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <StatBento label="Total edits"    value={totalEdits.toLocaleString()} sub="all time"          icon="edit"     tone="accent" />
          <StatBento label="Sites edited"   value={sitesEdited}                  sub="unique sites"       icon="globe"    tone="info" />
          <StatBento label="Active editors" value={activeEditors}                sub="across your team"   icon="users"    tone="success" />
          <StatBento label="Avg per day"    value={avgPerDay}                    sub="last 30 days"       icon="activity" tone="warning" />
        </div>

        {/* Filter bar */}
        <div className="filter-bar">
          <I.search style={{ color: 'var(--text-muted)', fontSize: 16 }} />
          <input
            className="input"
            placeholder="Search edits, sections, summaries…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="vdiv" style={{ height: 22 }} />
          <FilterDropdown icon={<I.activity />} label="Date range" value={filters.range} options={RANGE_OPTIONS} onChange={(v) => setFilter('range', v)} />
          <FilterDropdown icon={<I.globe />} label="All sites" value={filters.siteId} options={siteOptions} onChange={(v) => setFilter('siteId', v)} />
          <FilterDropdown icon={<I.user />} label="All users" value={filters.userId} options={userOptions} onChange={(v) => setFilter('userId', v)} />

          {/* More filters — edit type + action + clear */}
          <Popover
            align="right"
            render={(setOpen) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 220, padding: 4 }}>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', padding: '0 6px 4px' }}>Edit type</div>
                  {editTypeOptions.map(o => (
                    <button key={o.value || 'all'} style={menuItemStyle(o.value === filters.editType)} onClick={() => setFilter('editType', o.value)}>
                      <span className="truncate">{o.label}</span>
                      {o.value === filters.editType && <span style={{ marginLeft: 'auto' }}><I.check style={{ fontSize: 13 }} /></span>}
                    </button>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-faint)', padding: '0 6px 4px' }}>Action</div>
                  {ACTION_OPTIONS.map(o => (
                    <button key={o.value || 'all'} style={menuItemStyle(o.value === filters.action)} onClick={() => setFilter('action', o.value)}>
                      <span className="truncate">{o.label}</span>
                      {o.value === filters.action && <span style={{ marginLeft: 'auto' }}><I.check style={{ fontSize: 13 }} /></span>}
                    </button>
                  ))}
                </div>
                {activeFilterCount > 0 && (
                  <button className="btn btn-sm" style={{ justifyContent: 'center' }} onClick={() => { clearFilters(); setOpen(false) }}>
                    <I.x /> Clear all filters
                  </button>
                )}
              </div>
            )}
          >
            {(open, setOpen) => (
              <button
                className="btn btn-sm"
                style={{ marginLeft: 'auto', whiteSpace: 'nowrap', borderColor: (filters.editType || filters.action) ? 'var(--accent)' : undefined }}
                onClick={() => setOpen(o => !o)}
              >
                <I.filter /> More filters
                {activeFilterCount > 0 && (
                  <span style={{ fontSize: 10, background: 'var(--accent)', color: '#fff', borderRadius: 999, padding: '1px 6px', fontWeight: 700 }}>{activeFilterCount}</span>
                )}
              </button>
            )}
          </Popover>
        </div>

        {/* Table */}
        {error ? (
          <div className="card card-pad" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <p style={{ marginBottom: 12 }}>Couldn’t load edit logs.</p>
            <button className="btn btn-sm" onClick={refetch}>Retry</button>
          </div>
        ) : (
          <>
            <div className="sites-table">
              <div className="row header" style={{ gridTemplateColumns: GRID }}>
                <div>Timestamp</div><div>User</div><div>Site</div><div>Section</div>
                <div>Action</div><div>Change summary</div><div></div>
              </div>
              {isLoading ? (
                <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>
                  Loading edit logs…
                </div>
              ) : !logs.length ? (
                <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-faint)' }}>
                  No edit logs found.
                </div>
              ) : (
                logs.map(log => (
                  <LogRow
                    key={log.id}
                    log={log}
                    open={openId === log.id}
                    onToggle={() => setOpenId(openId === log.id ? null : log.id)}
                    onView={() => window.open(`/admin/sites/${log.site_id}/preview`, '_blank')}
                    onEditor={() => window.open(`/admin/editor?site=${log.site_id}`, '_blank')}
                    onRevert={() => handleRevert(log)}
                    reverting={revertMutation.isLoading && revertMutation.variables === log.id}
                  />
                ))
              )}
            </div>

            {!search && <Pagination pagination={logsData?.pagination} onPageChange={setPage} />}
          </>
        )}
      </div>
    </div>
  )
}

export default EditLogsDashboard
